import { useEffect, useRef } from "react";
import * as maplibregl from "maplibre-gl";
import type { GeoJSONSource, LngLatBoundsLike, MapLayerMouseEvent } from "maplibre-gl";
import type * as GeoJSON from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";
import { token } from "./tokens";

export interface MapItem { id: string; label: string; geometry: string | null; latitude?: string | null; longitude?: string | null; kind?: string; tone?: "primary" | "warning" | "info" | "audio"; sub?: string }

// Satellite basemap: Esri World Imagery + reference labels (no key needed).
// Field boundaries drawn from synced map_features geometry.
const STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    imagery: { type: "raster", tileSize: 256, attribution: "Esri, Maxar, Earthstar Geographics", tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"] },
    labels: { type: "raster", tileSize: 256, tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"] },
  },
  layers: [{ id: "imagery", type: "raster", source: "imagery" }, { id: "labels", type: "raster", source: "labels", paint: { "raster-opacity": 0.85 } }],
};

function toFeatures(items: MapItem[]) {
  const features: GeoJSON.Feature[] = [];
  for (const it of items) {
    let g: GeoJSON.Geometry | null;
    try { g = it.geometry ? JSON.parse(it.geometry) : null; } catch { g = null; }
    if (g && (g as any).type === "Feature") g = (g as any).geometry;
    // Real data isn't always a single geometry: collections are flattened and
    // anything without a coordinates array falls back to the lat/lng point.
    const parts = geometries(g);
    if (!parts.length && it.latitude && it.longitude) parts.push({ type: "Point", coordinates: [+it.longitude, +it.latitude] });
    for (const geometry of parts) features.push({ type: "Feature", id: it.id, geometry, properties: { id: it.id, label: it.label, tone: it.tone ?? "primary", sub: it.sub ?? "" } });
  }
  return { type: "FeatureCollection" as const, features };
}

function geometries(g: any): GeoJSON.Geometry[] {
  if (!g || typeof g !== "object") return [];
  if (g.type === "GeometryCollection") return (g.geometries ?? []).flatMap(geometries);
  if (g.type === "FeatureCollection") return (g.features ?? []).flatMap((f: any) => geometries(f?.geometry));
  if (g.type === "Feature") return geometries(g.geometry);
  return Array.isArray(g.coordinates) && g.coordinates.length ? [g] : [];
}

function bounds(fc: GeoJSON.FeatureCollection): LngLatBoundsLike | null {
  const b = new maplibregl.LngLatBounds();
  let any = false;
  const walk = (c: any) => { if (!Array.isArray(c) || !c.length) return; if (typeof c[0] === "number") { b.extend(c as [number, number]); any = true; } else c.forEach(walk); };
  fc.features.forEach((f: GeoJSON.Feature) => walk((f.geometry as any).coordinates));
  return any ? b : null;
}

export function LocationMap({ items, height = 360, selectedId, onSelect, interactive = true }: { items: MapItem[]; height?: number | string; selectedId?: string | null; onSelect?: (id: string) => void; interactive?: boolean }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const onSelectRef = useRef(onSelect);
  const ready = useRef<(() => void)[] | null>([]);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!el.current) return;
    const m = new maplibregl.Map({ container: el.current, style: STYLE, center: [-93.6, 42.0], zoom: 9, attributionControl: { compact: true }, interactive, cooperativeGestures: false });
    map.current = m;
    if (interactive) m.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    m.on("load", () => {
      const primary = token("--pm-primary-light"), warn = token("--pm-warning"), info = token("--pm-info"), audio = token("--pm-data-audio"), white = token("--pm-surface");
      const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 10 });
      const tone: any = ["match", ["get", "tone"], "warning", warn, "info", info, "audio", audio, primary];
      m.addSource("items", { type: "geojson", data: { type: "FeatureCollection", features: [] }, promoteId: "id" });
      m.addLayer({ id: "fill", type: "fill", source: "items", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": tone, "fill-opacity": ["case", ["boolean", ["feature-state", "sel"], false], 0.42, 0.22] } });
      m.addLayer({ id: "line", type: "line", source: "items", filter: ["==", ["geometry-type"], "Polygon"], paint: { "line-color": ["case", ["boolean", ["feature-state", "sel"], false], white, tone], "line-width": ["case", ["boolean", ["feature-state", "sel"], false], 3, 2] } });
      // Small fields vanish at regional zoom; show them as dots until they're big enough to read.
      m.addSource("centroids", { type: "geojson", data: { type: "FeatureCollection", features: [] }, promoteId: "id" });
      m.addLayer({ id: "cdots", type: "circle", source: "centroids", maxzoom: 12, paint: { "circle-radius": 6, "circle-color": tone, "circle-stroke-color": white, "circle-stroke-width": 2, "circle-opacity": ["interpolate", ["linear"], ["zoom"], 10.5, 1, 12, 0], "circle-stroke-opacity": ["interpolate", ["linear"], ["zoom"], 10.5, 1, 12, 0] } });
      m.addLayer({ id: "pts", type: "circle", source: "items", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": ["case", ["boolean", ["feature-state", "sel"], false], 9, 7], "circle-color": tone, "circle-stroke-color": white, "circle-stroke-width": 2.5 } });
      for (const layer of ["fill", "pts", "cdots"]) {
        m.on("click", layer, (e: MapLayerMouseEvent) => { const id = e.features?.[0]?.properties?.id; if (id) onSelectRef.current?.(id); });
        m.on("mousemove", layer, (e: MapLayerMouseEvent) => {
          m.getCanvas().style.cursor = "pointer";
          const p = e.features?.[0]?.properties;
          if (p) popup.setLngLat(e.lngLat).setHTML(`<strong>${escapeHtml(p.label)}</strong>${p.sub ? `<div class="subtle">${escapeHtml(p.sub)}</div>` : ""}`).addTo(m);
        });
        m.on("mouseleave", layer, () => { m.getCanvas().style.cursor = ""; popup.remove(); });
      }
      const q = ready.current || [];
      ready.current = null;
      q.forEach((f) => f());
    });
    return () => { m.remove(); map.current = null; ready.current = []; };
  }, [interactive]);

  const key = items.map((i) => i.id + (i.tone ?? "")).join("|");
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const apply = () => {
      const fc = toFeatures(items);
      (m.getSource("items") as GeoJSONSource | undefined)?.setData(fc);
      (m.getSource("centroids") as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: fc.features.filter((f) => f.geometry.type === "Polygon" && Array.isArray((f.geometry as GeoJSON.Polygon).coordinates[0])).map((f) => ({ type: "Feature", id: f.id, properties: f.properties, geometry: { type: "Point", coordinates: centroid((f.geometry as GeoJSON.Polygon).coordinates[0]) } })) });
      const b = bounds(fc);
      if (b) m.fitBounds(b, { padding: 48, maxZoom: 15, duration: 0 });
    };
    if (ready.current) ready.current.push(apply); else apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const m = map.current;
    if (!m || !m.getSource("items")) return;
    m.removeFeatureState({ source: "items" });
    if (selectedId) {
      m.setFeatureState({ source: "items", id: selectedId }, { sel: true });
      const it = items.find((i) => i.id === selectedId);
      const fc = it ? toFeatures([it]) : null;
      const b = fc && bounds(fc);
      if (b) m.fitBounds(b, { padding: 80, maxZoom: 15.5, duration: 600 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return <div className="mapbox" style={{ height }} ref={el} />;
}

function escapeHtml(s: string) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function centroid(ring: number[][]): [number, number] {
  let x = 0, y = 0;
  const n = Math.max(1, ring.length - 1);
  for (let i = 0; i < n; i++) { x += ring[i][0]; y += ring[i][1]; }
  return [x / n, y / n];
}
